import * as React from 'react';
import {
  useAssets,
  usePreviewMode,
  useComponentPreviewMode,
  useAnalyzeMode,
  useAnnotationsMode,
  useBuildMode,
  useSite,
  useRuntimeEnvironment,
  useCodeComponentsNestedRoot,
  useIdAttrMap,
  useCurrentLocale,
  useFunctionsPreview,
  useCodeModuleWorkerUrlMap,
  useCssVariables,
  useCssVariableCollections,
  useSlugMap,
} from '@packages/systems/render';
import {
  buildFunctionsPreviewBaseUrl,
  buildFunctionsPreviewHeaders,
} from '@packages/systems/code-modules/constants/functions';
import {handleCdnUrl} from '@packages/systems/core/utils/UrlUtils';
import {hrefAttr} from '@packages/systems/core/utils/HtmlUtils';
import {SerializeContext} from '@packages/systems/code-libraries/propTypes';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {buildCodeLibraryAliasSheets} from '@packages/systems/code-libraries/alias-sheets';
import {makeCodeModuleId} from '@packages/systems/code-modules/core';
import type * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import type {Plugins} from '@packages/systems/wfdl';
import {singleMemo} from '@packages/utilities/memo';
import {
  clearRenderCheckpoints,
  verifyRenderCheckpoints,
  type IRendererPrivateWebflowContext,
  type IRendererPublicWebflowContext,
} from './CodeIslandSharedUtils';
import {
  useCodeIslandCustomElementState,
  useUpdateCodeIslandCustomElementState,
} from './siteModules/CodeIslandCustomElementStoreHooks';

/**
 * Custom hook to provide SerializeContext for serializing props in CodeIsland.
 */
export function useSerializeContext(
  hrefMap: Record<string, string>
): SerializeContext {
  const site = useSite();
  const assets = useAssets();
  const idAttrMap = useIdAttrMap();
  const runtimeEnvironment = useRuntimeEnvironment();

  const getAsset = React.useCallback(
    (id: string) => {
      const asset = assets[id];
      if (!asset) return undefined;
      return {
        ...asset,
        src: handleCdnUrl(site, runtimeEnvironment)(asset.cdnUrl),
      };
    },
    [site, assets, runtimeEnvironment]
  );

  const getIdAttr = React.useCallback(
    (id: string) => idAttrMap[id],
    [idAttrMap]
  );

  const sanitizeHref = React.useCallback(
    (href: string) => {
      return hrefAttr(href, runtimeEnvironment).href;
    },
    [runtimeEnvironment]
  );

  const getPageHref = React.useCallback(
    ({pageId, pageSlug}: {pageId?: string; pageSlug?: string}) => {
      const prefix = site.pageLinkHrefPrefix;
      const slug = pageId ? hrefMap[pageId] : pageSlug;
      return slug ? `${prefix}${slug}` : undefined;
    },
    [site.pageLinkHrefPrefix, hrefMap]
  );

  return {
    getAsset,
    getIdAttr,
    getPageHref,
    sanitizeHref,
  };
}

type CodeComponentsWebflowContext = {
  publicContext: IRendererPublicWebflowContext;
  privateContext: IRendererPrivateWebflowContext;
};

/**
 * Custom hook that provides the webflow context for code islands, split into
 * the half that is safe to serialize into the DOM and the half that is not.
 */
export function useCodeComponentsWebflowContext({
  moduleId,
  functionsPreviewModuleId = moduleId,
}: {
  moduleId: string | null;
  functionsPreviewModuleId?: string | null;
}): CodeComponentsWebflowContext {
  const runtimeEnvironment = useRuntimeEnvironment();
  const site = useSite();
  const isAnalyzeMode = useAnalyzeMode();
  const isAnnotationsMode = useAnnotationsMode();
  const isBuild = useBuildMode();
  const isComponentPreviewMode = useComponentPreviewMode();
  const isPreviewMode = usePreviewMode();
  const currentLocale = useCurrentLocale();
  const functionsPreview = useFunctionsPreview();
  const codeModuleWorkerUrlMap = useCodeModuleWorkerUrlMap();

  const getMode = (): IRendererPublicWebflowContext['mode'] => {
    if (isAnalyzeMode) {
      return 'analyze';
    }
    if (isAnnotationsMode) {
      return 'comment';
    }
    if (isPreviewMode) {
      return 'preview';
    }
    if (isBuild) {
      return 'build';
    }
    if (isComponentPreviewMode) {
      return 'component-preview';
    }
    if (runtimeEnvironment === WebflowEnvironments.DESIGNER) {
      return 'design';
    }
    return 'publish';
  };

  const mode = getMode();
  const interactive = mode === 'preview' || mode === 'publish';
  const locale = currentLocale?.tag || site.lang || null;

  const publicFunctionsBaseUrl = moduleId
    ? codeModuleWorkerUrlMap?.[moduleId]?.functions
    : undefined;

  // The half CodeIsland serializes into the DOM.
  const publicContext = React.useMemo((): IRendererPublicWebflowContext => {
    return {
      mode,
      interactive,
      locale,
      ...(publicFunctionsBaseUrl
        ? {functions: {baseUrl: publicFunctionsBaseUrl}}
        : {}),
    };
  }, [mode, interactive, locale, publicFunctionsBaseUrl]);

  // The half that travels to the element as a JS property instead.
  const privateContext = React.useMemo((): IRendererPrivateWebflowContext => {
    // Only the Designer populates this channel.
    if (!functionsPreview) return {};
    if (!functionsPreviewModuleId) return {};
    // SITE callers propose siteId; the host config confirms it belongs to the
    // selected preview before exposing private Functions routing.
    if (
      moduleId === null &&
      functionsPreview.siteModuleId !== functionsPreviewModuleId
    )
      return {};

    if (functionsPreview.credentials) {
      // `readiness` is redundant here, see IRendererFunctionsContext.
      return {
        functions: {
          baseUrl: buildFunctionsPreviewBaseUrl(
            functionsPreview.credentials.origin,
            functionsPreviewModuleId
          ),
          headers: buildFunctionsPreviewHeaders(
            functionsPreview.credentials.token
          ),
        },
      };
    }

    // Key order is fixed here because the `privateWebflowContext` setter
    // dedupes on JSON.
    return {
      functions: {
        readiness: functionsPreview.readiness,
      },
    };
  }, [moduleId, functionsPreviewModuleId, functionsPreview]);

  return {publicContext, privateContext};
}

/**
 * Custom hook that handles Code Components safe mode
 */
export function useCodeComponentsSafeModeEnabled() {
  const site = useSite();
  const runtimeEnvironment = useRuntimeEnvironment();
  return (
    Boolean(site.codeComponentsSafeModeReason) &&
    runtimeEnvironment === WebflowEnvironments.DESIGNER
  );
}

/**
 * Custom hook that determines if the new nested root should be enabled.
 * This is only disabled in SSR for existing published sites.
 */
export function useCodeComponentsNestedRootEnabled() {
  const runtimeEnvironment = useRuntimeEnvironment();
  const nestedRootEnabled = useCodeComponentsNestedRoot();
  if (runtimeEnvironment !== WebflowEnvironments.SSR) {
    // Always enabled in Designer and other non SSR environments
    return true;
  }
  // This is true for new site publishes - and undefined for existing sites
  return Boolean(nestedRootEnabled);
}

export function useRenderCheckpoints(
  designerId: string | null | undefined,
  onRenderError?: () => void
) {
  const updateCodeIslandState =
    useUpdateCodeIslandCustomElementState(designerId);
  const [renderVerified, setRenderVerified] = React.useState<
    boolean | undefined
  >(undefined);
  const codeIslandState = useCodeIslandCustomElementState(designerId);

  const resetRenderCheckpoints = React.useCallback(() => {
    clearRenderCheckpoints(designerId);
    setRenderVerified(undefined);
  }, [designerId, setRenderVerified]);

  React.useEffect(() => {
    // if we have already verified the render checkpoints, we don't need to verify them again
    if (renderVerified !== undefined || codeIslandState?.status === 'error') {
      return;
    }

    const verified = verifyRenderCheckpoints(designerId);
    setRenderVerified(verified);
    if (!verified) {
      // if the last render was not successful, we want to show the error state since it's possible
      // the error was due to a bad render
      updateCodeIslandState({
        status: 'error',
        error: new Error('Render failed'),
      });
      onRenderError && onRenderError();
    } else {
      // if the last render was successful, we want to clear the checkpoints so we can verify again next time
      clearRenderCheckpoints(designerId);
    }
  }, [
    designerId,
    updateCodeIslandState,
    setRenderVerified,
    renderVerified,
    codeIslandState?.status,
    onRenderError,
  ]);

  return {renderVerified, resetRenderCheckpoints};
}

// One computation per store state, shared by every island
const getAliasSheets = singleMemo((variables: StyleVariables.VariableMap) =>
  singleMemo((collections: StyleVariables.VariableCollectionMap) =>
    singleMemo((slugMap: Plugins.PluginsType['slugMap']) =>
      buildCodeLibraryAliasSheets({variables, collections, slugMap})
    )
  )
);

/**
 * The alias sheet of a code module, built from the Designer's variables.
 * Null outside the Designer, where the variables are empty.
 */
export function useVariableAliasCss(moduleId: string | null): string | null {
  const variables = useCssVariables();
  const collections = useCssVariableCollections();
  const slugMap = useSlugMap();
  const sheets = getAliasSheets(variables)(collections)(slugMap);
  return moduleId ? (sheets.get(makeCodeModuleId(moduleId)) ?? null) : null;
}
